/**
 * DomainCoverageSummary.jsx
 * data-fit LLC — DecisionFlow
 * Stage 2 CONFIGURE — CONF-05: Domain coverage summary
 * Branch: feature/compliance-lifecycle-foundation
 * Spec authority: ComponentSpec_CONFIGURE.md (S82) + MasterContext_S88_Close.md
 * Path: ui/src/components/tenant-admin/DomainCoverageSummary.jsx
 *
 * S89 audit patches applied: PATCH-04, PATCH-22 — see original for notes.
 * OI items remain open — see original header.
 * M040 C2: hardcoded colors → tokens, text-* → text-tok-*, all <td> get text-[rgb(var(--color-text))]
 * S495: Authorization: Bearer token added to all fetch calls (OI-ONBOARDING-AUTH-HEADER-PATTERN)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { AI_TIMEOUT_MS, timeoutPromise } from './ConfigureFlowShell';

const API_BASE = '/api/v1';

function normalizeResponse(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

function getErrorMessage(error) {
  return error?.message || 'Something went wrong.';
}

function getUserId(user) {
  return user.user_id || user.id || user.user_account_id;
}

export default function DomainCoverageSummary({
  tenantId, tier,
  aiStatus, aiResponse,
  onAiStatusChange, onAiResponseReceived,
  onBack, onContinue
}) {
  const { token } = useAuth();

  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [segments, setSegments] = useState([]);
  const [geoNodes, setGeoNodes] = useState([]);
  const [coverageMatrix, setCoverageMatrix] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const isEnterprise = tier === 'enterprise';

  const authHeaders = useMemo(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const buildCoverageMatrix = useCallback((buRows, segmentRows, assignmentRows, userRows) => {
    const matrix = [];
    buRows.forEach((bu) => {
      segmentRows.forEach((segment) => {
        const matchingAssignments = assignmentRows.filter((assignment) => {
          const buMatches = assignment.business_unit_id == null || String(assignment.business_unit_id) === String(bu.business_unit_id);
          const segmentMatches = assignment.segment_id == null || String(assignment.segment_id) === String(segment.segment_id);
          return buMatches && segmentMatches && !assignment.revoked_at;
        });
        const ownerNames = matchingAssignments.map((assignment) => {
          const owner = userRows.find((u) => String(getUserId(u)) === String(assignment.user_id || assignment.user_account_id));
          return owner?.full_name || owner?.fullName || owner?.email || assignment.user_id;
        }).filter(Boolean);
        matrix.push({
          key: `${bu.business_unit_id}:${segment.segment_id}`,
          businessUnitId: bu.business_unit_id,
          businessUnitName: bu.bu_name || bu.bu_code,
          segmentId: segment.segment_id,
          segmentName: segment.segment_name || segment.segment_code,
          ownerNames,
          covered: ownerNames.length > 0
        });
      });
    });
    return matrix;
  }, []);

  const fetchCoverageData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [usersResponse, assignmentsResponse, buResponse, segmentResponse] = await Promise.all([
        fetch(`${API_BASE}/users`, { credentials: 'include', headers: authHeaders }),
        fetch(`${API_BASE}/user-role-assignments`, { credentials: 'include', headers: authHeaders }),
        fetch(`${API_BASE}/business-units`, { credentials: 'include', headers: authHeaders }),
        fetch(`${API_BASE}/segments`, { credentials: 'include', headers: authHeaders })
      ]);

      const failed = [usersResponse, assignmentsResponse, buResponse, segmentResponse].find((r) => !r.ok);
      if (failed) {
        const payload = await failed.json().catch(() => ({}));
        throw new Error(payload?.error?.message || 'Failed to load coverage data');
      }

      const [usersPayload, assignmentsPayload, buPayload, segmentPayload] = await Promise.all([
        usersResponse.json(), assignmentsResponse.json(), buResponse.json(), segmentResponse.json()
      ]);

      const nextUsers         = normalizeResponse(usersPayload);
      const nextAssignments   = normalizeResponse(assignmentsPayload);
      const nextBusinessUnits = normalizeResponse(buPayload);
      const nextSegments      = normalizeResponse(segmentPayload);

      setUsers(nextUsers);
      setAssignments(nextAssignments);
      setBusinessUnits(nextBusinessUnits);
      setSegments(nextSegments);
      setCoverageMatrix(buildCoverageMatrix(nextBusinessUnits, nextSegments, nextAssignments, nextUsers));

      if (isEnterprise) {
        const geoResults = [];
        for (const currentUser of nextUsers) {
          const currentUserId = getUserId(currentUser);
          if (!currentUserId) continue;
          const response = await fetch(
            `${API_BASE}/user-access-scope?user_id=${encodeURIComponent(currentUserId)}`,
            { credentials: 'include', headers: authHeaders }
          );
          if (response.ok) {
            const payload = await response.json();
            geoResults.push(...normalizeResponse(payload).map((scope) => ({ ...scope, user_id: currentUserId })));
          }
        }
        setGeoNodes(geoResults);
      }
    } catch (error) {
      setLoadError(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [buildCoverageMatrix, isEnterprise, authHeaders]);

  const runScopeAdvisor = useCallback(async () => {
    if (tier === 'essentials') return;
    onAiStatusChange('loading');
    try {
      const aiRequest = fetch(`${API_BASE}/ai-queries`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: JSON.stringify({ panel_context: 'configure.scope_advisor' })
      });

      const response = await Promise.race([aiRequest, timeoutPromise(AI_TIMEOUT_MS)]);

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        if (payload?.error?.code === 'query_blocked' || payload?.query_blocked === true) { onAiStatusChange('blocked'); return; }
        throw new Error(payload?.error?.message || 'Scope Advisor failed');
      }

      const payload = await response.json();
      if (payload?.query_blocked === true) { onAiStatusChange('blocked'); return; }

      const responseItems = payload?.ai_response?.recommendations || payload?.ai_response?.items || payload?.recommendations || payload?.items || [];
      onAiResponseReceived(responseItems);
      onAiStatusChange('ready');
    } catch (_error) {
      onAiStatusChange('error');
    }
  }, [onAiResponseReceived, onAiStatusChange, tier, authHeaders]);

  useEffect(() => {
    if (tier === 'essentials') return;
    fetchCoverageData();
  }, [fetchCoverageData, tier]);

  // PATCH-04: guard against double-fire on mount
  useEffect(() => {
    if (tier === 'essentials') return;
    if (aiStatus !== 'idle') return;
    runScopeAdvisor();
  }, [aiStatus, runScopeAdvisor, tier]);

  const uncoveredCount = useMemo(() => coverageMatrix.filter((row) => !row.covered).length, [coverageMatrix]);

  if (tier === 'essentials') {
    return (
      <section className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-8 shadow-sm">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Domain coverage is not available on Essentials</h2>
        <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">Essentials tenants continue directly to configure gate summary.</p>
        <button type="button" onClick={onContinue} className="mt-5 rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))]">Continue</button>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-sm">
      <div className="border-b border-[rgb(var(--color-border))] p-5">
        <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">CONF-05</p>
        <h2 className="mt-1 text-tok-xl font-semibond text-[rgb(var(--color-text))]">Domain coverage summary</h2>
        <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Review BU × segment coverage before opening setup. Standard tenants receive Scope Advisor guidance; Enterprise tenants also include geo scope review.
        </p>
      </div>

      <div className="space-y-5 p-5">
        {loadError && (
          <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-4 text-tok-sm text-[rgb(var(--color-danger))]">
            <p className="font-semibold">Unable to load coverage data</p>
            <p className="mt-1">{loadError}</p>
            <button type="button" onClick={fetchCoverageData} className="mt-3 rounded-lg bg-[rgb(var(--color-danger))] px-3 py-2 text-white">Retry</button>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          {[
            { label: 'Users', value: users.length },
            { label: 'Coverage cells', value: coverageMatrix.length },
            { label: 'Uncovered', value: uncoveredCount },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
              <p className="text-tok-xs font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">{label}</p>
              <p className="mt-1 text-tok-2xl font-semibold text-[rgb(var(--color-text))]">{value}</p>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
          <table className="min-w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
            <thead className="bg-[rgb(var(--color-surface-muted))]">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Business Unit</th>
                <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Segment</th>
                <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Coverage</th>
                <th className="px-4 py-3 text-left font-semibold text-[rgb(var(--color-text))]">Comp Admins</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
              {isLoading ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-[rgb(var(--color-text-muted))]">Loading coverage...</td></tr>
              ) : coverageMatrix.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-[rgb(var(--color-text-muted))]">No coverage rows found.</td></tr>
              ) : (
                coverageMatrix.map((row) => (
                  <tr key={row.key}>
                    <td className="px-4 py-3 text-[rgb(var(--color-text))]">{row.businessUnitName}</td>
                    <td className="px-4 py-3 text-[rgb(var(--color-text))]">{row.segmentName}</td>
                    <td className="px-4 py-3 text-[rgb(var(--color-text))]">
                      <span className={`rounded-full px-2 py-1 text-tok-xs font-medium ${row.covered ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]'}`}>
                        {row.covered ? 'Covered' : 'Uncovered'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[rgb(var(--color-text))]">{row.ownerNames.length > 0 ? row.ownerNames.join(', ') : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {isEnterprise && (
          <div className="rounded-xl border border-[rgb(var(--color-border))] p-4">
            <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Geo coverage</h3>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">{geoNodes.length} active geo scope rows found.</p>
          </div>
        )}

        <div className="rounded-xl border border-[rgb(var(--color-border))] p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Scope Advisor</h3>
              <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">AI-assisted review of scope risk and coverage gaps.</p>
            </div>
            <button type="button" onClick={runScopeAdvisor} className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]">
              Re-run
            </button>
          </div>

          <div className="mt-4">
            {aiStatus === 'idle' && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Scope Advisor has not run yet.</p>}
            {aiStatus === 'loading' && <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Scope Advisor is reviewing coverage...</p>}
            {aiStatus === 'timeout' && (
              <div className="rounded-xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 p-3 text-tok-sm text-[rgb(var(--color-warning))]">
                Scope Advisor timed out. You can continue or retry.
              </div>
            )}
            {aiStatus === 'blocked' && (
              <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
                Scope Advisor response was blocked.
              </div>
            )}
            {aiStatus === 'error' && (
              <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-3 text-tok-sm text-[rgb(var(--color-danger))]">
                Scope Advisor could not complete.
              </div>
            )}
            {/* Null-safe AI render: both conditions required per locked rule 13 */}
            {aiStatus === 'ready' && aiResponse?.length > 0 && (
              <ul className="space-y-3">
                {aiResponse.map((item, index) => (
                  <li key={`${item?.id || item?.title || 'advisor'}-${index}`} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3 text-tok-sm">
                    <p className="font-semibold text-[rgb(var(--color-text))]">{item?.title || item?.risk || `Recommendation ${index + 1}`}</p>
                    <p className="mt-1 text-[rgb(var(--color-text-muted))]">{item?.message || item?.description || item?.recommendation || String(item)}</p>
                  </li>
                ))}
              </ul>
            )}
            {aiStatus === 'ready' && (!aiResponse || aiResponse.length === 0) && (
              <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Scope Advisor completed with no recommendations.</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-[rgb(var(--color-border))] p-5">
        <button type="button" onClick={onBack} className="rounded-xl border border-[rgb(var(--color-border))] px-5 py-2.5 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]">Back</button>
        <button type="button" onClick={onContinue} className="rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))]">Continue</button>
      </div>
    </section>
  );
}
